import { useEffect, useState } from "react";
import { AudioSettingsFields } from "../../components/AudioSettingsFields";
import { toast } from "sonner";
import { Webhook, Save, Zap, HeartPulse, PhoneOutgoing, UserCog, Download, Upload, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export function SettingsTab() {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const { refresh } = useAuth();
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [emailForm, setEmailForm] = useState({ email: "", password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [audio, setAudio] = useState(null);
  const [audioSaving, setAudioSaving] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/admin/account/password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Admin password updated — effective immediately");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwSaving(false);
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmailSaving(true);
    try {
      const res = await api.post("/admin/account/email", { new_email: emailForm.email, password: emailForm.password });
      toast.success(`Admin username updated to ${res.data.username} — effective immediately`);
      setEmailForm({ email: "", password: "" });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailSaving(false);
    }
  };

  const downloadBackup = async () => {
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scharpz-live-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRestoring(true);
    try {
      const backup = JSON.parse(await file.text());
      const res = await api.post("/admin/backup/restore", { backup });
      const total = Object.values(res.data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Backup restored — ${total} records recovered`);
    } catch (err) {
      toast.error(err instanceof SyntaxError ? "That file is not valid JSON" : formatApiError(err));
    } finally {
      setRestoring(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    api.get("/admin/settings").then((res) => {
      setWebhookUrl(res.data.alert_webhook_url || "");
      setAudio(res.data.audio || null);
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", { alert_webhook_url: webhookUrl });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveAudio = async (e) => {
    e.preventDefault();
    setAudioSaving(true);
    try {
      await api.put("/admin/settings", { alert_webhook_url: webhookUrl, audio });
      toast.success("Audio settings applied — Asterisk reloaded with the new config");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAudioSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await api.post("/admin/settings/test-webhook");
      toast.success(`Test webhook delivered — HTTP ${res.data.status_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-tab">
      <div className="ops-card p-5 max-w-2xl">
        <div className="ops-caption mb-1 flex items-center gap-1.5">
          <Webhook className="w-3.5 h-3.5" /> Offline Alert Webhook
        </div>
        <h3 className="text-lg font-semibold text-slate-100 mb-1">Get alerted before residents complain</h3>
        <p className="text-sm text-slate-400 mb-4">
          Every 2 minutes the backend checks each intercom's presence (SIP registration state, or device
          heartbeat). When one goes dark — or comes back — we POST a JSON event to this URL.
          Point it at anything that accepts webhooks (your automation, ntfy, Zapier, a small script…).
        </p>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Webhook URL</Label>
            <Input data-testid="webhook-url-input" value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="bg-[#0A0D14] border-[#1E2638] font-mono"
              placeholder="https://hooks.example.com/scharpz-alerts" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" data-testid="webhook-save-btn" disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
            <Button type="button" data-testid="webhook-test-btn" onClick={test} disabled={testing || !webhookUrl}
              variant="outline" className="border-[#1E2638] bg-transparent hover:bg-[#1E2638] text-slate-300">
              <Zap className="w-4 h-4 mr-1.5" /> Send Test
            </Button>
          </div>
        </form>
        <div className="mt-4 bg-[#0A0D14] border border-[#1E2638] rounded-lg p-3">
          <div className="ops-caption mb-2">Event payload example</div>
          <pre className="text-xs font-mono text-slate-400 overflow-x-auto">{`{
  "event": "intercom_offline",   // or "intercom_online"
  "extension": "100",
  "label": "Main Gate Intercom",
  "site_id": "…", "server_id": "…",
  "timestamp": "2026-09-13T12:00:00+00:00"
}`}</pre>
        </div>
      </div>

      <div className="ops-card p-5 max-w-2xl" data-testid="heartbeat-info-card">
        <div className="ops-caption mb-1 flex items-center gap-1.5">
          <HeartPulse className="w-3.5 h-3.5" /> Device Heartbeat (optional)
        </div>
        <p className="text-sm text-slate-400 mb-3">
          Intercoms that can't register via SIP can still report liveness — point them (or a cron on their LAN)
          at this endpoint every few minutes with their device API key:
        </p>
        <div className="bg-[#0A0D14] border border-[#1E2638] rounded-lg p-3 font-mono text-xs text-emerald-400">
          POST /api/heartbeat<br />
          Header: X-Device-Key: &lt;intercom api key&gt;
        </div>
        <p className="text-xs text-slate-500 mt-3">
          An intercom with no SIP registration and no heartbeat for 15 minutes counts as offline.
        </p>
      </div>

      <div className="ops-card p-5 max-w-2xl" data-testid="browser-audio-info-card">
        <div className="ops-caption mb-1 flex items-center gap-1.5">
          <PhoneOutgoing className="w-3.5 h-3.5" /> Browser Live Audio (WebRTC)
        </div>
        <p className="text-sm text-slate-400">
          Tenant extensions automatically get a hidden browser identity (<span className="font-mono text-cyan-400">w&lt;number&gt;</span>).
          When the tenant portal is open it registers over secure WebSocket — intercom calls then ring the
          tenant's softphone <em>and</em> their browser, with two-way audio on Accept. No extra setup needed
          as long as the <span className="font-mono">/ws</span> proxy block from the deploy README is in the site config.
        </p>
      </div>

      <div className="ops-card p-5 max-w-2xl" data-testid="audio-quality-card">
        <div className="ops-caption mb-1 flex items-center gap-1.5">
          <PhoneOutgoing className="w-3.5 h-3.5" /> Audio Quality
        </div>
        <h3 className="text-lg font-semibold text-slate-100 mb-1">Call sound tuning</h3>
        <p className="text-sm text-slate-400 mb-4">
          Applied to Asterisk the moment you save — no redeploy needed. Change one thing, make a test call, repeat.
        </p>
        {audio && (
          <form onSubmit={saveAudio} className="space-y-5">
            <AudioSettingsFields audio={audio} setAudio={setAudio} idPrefix="audio" />
            <Button type="submit" data-testid="audio-save-btn" disabled={audioSaving || !audio.codecs.length}
              className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
              {audioSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Apply Audio Settings</>}
            </Button>
          </form>
        )}
      </div>

      <div className="ops-card p-5 max-w-2xl" data-testid="admin-account-card">
        <div className="ops-caption mb-1 flex items-center gap-1.5">
          <UserCog className="w-3.5 h-3.5" /> Admin Account
        </div>
        <h3 className="text-lg font-semibold text-slate-100 mb-4">Credentials — effective immediately</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <form onSubmit={changePassword} className="space-y-3">
            <div className="ops-caption">Change Password</div>
            <Input data-testid="admin-current-password-input" type="password" required placeholder="Current password"
              value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
              className="bg-[#0A0D14] border-[#1E2638]" />
            <Input data-testid="admin-new-password-input" type="password" required minLength={8} placeholder="New password (min 8 chars)"
              value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
              className="bg-[#0A0D14] border-[#1E2638]" />
            <Input data-testid="admin-confirm-password-input" type="password" required placeholder="Confirm new password"
              value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              className="bg-[#0A0D14] border-[#1E2638]" />
            <Button type="submit" data-testid="admin-password-save-btn" disabled={pwSaving}
              className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold w-full">
              {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
            </Button>
          </form>
          <form onSubmit={changeEmail} className="space-y-3">
            <div className="ops-caption">Change Username / Email</div>
            <Input data-testid="admin-new-email-input" type="email" required placeholder="New username / email"
              value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
              className="bg-[#0A0D14] border-[#1E2638]" />
            <Input data-testid="admin-email-password-input" type="password" required placeholder="Confirm with current password"
              value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
              className="bg-[#0A0D14] border-[#1E2638]" />
            <Button type="submit" data-testid="admin-email-save-btn" disabled={emailSaving}
              className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold w-full">
              {emailSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Username"}
            </Button>
            <p className="text-xs text-slate-500">Your current session stays logged in; next login uses the new credentials.</p>
          </form>
        </div>
      </div>

      <div className="ops-card p-5 max-w-2xl" data-testid="backup-card">
        <div className="ops-caption mb-1 flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> Backup &amp; Restore
        </div>
        <h3 className="text-lg font-semibold text-slate-100 mb-1">Disaster recovery</h3>
        <p className="text-sm text-slate-400 mb-4">
          Download a full snapshot (sites, servers, extensions, users, settings, call logs) as a single JSON file.
          Restore replaces <strong className="text-slate-200">all</strong> current data with the file's contents —
          including accounts and passwords as they were at backup time.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="backup-download-btn" onClick={downloadBackup}
            className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
            <Download className="w-4 h-4 mr-1.5" /> Download Backup
          </Button>
          <label>
            <input type="file" accept=".json,application/json" className="hidden"
              data-testid="backup-upload-input" onChange={onRestoreFile} />
            <Button type="button" data-testid="backup-upload-btn" disabled={restoring} variant="outline"
              onClick={(e) => e.currentTarget.previousElementSibling?.click()}
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 bg-transparent">
              {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Upload Backup File</>}
            </Button>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Keep backups off-server (e.g. your PC). After a restore, log in with the credentials from the backup.
        </p>
      </div>
    </div>
  );
}
